// Application flow (spec §7–§10): details → review → submit. The submitted
// application is tracked on the next screen, where the KFS lives.
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { applyForLoan } from '@/api/loans';
import { ApiError } from '@/api/client';
import type { LoanDetails, RepaymentMethod } from '@/types/loan.types';
import {
  APPROVED_PROJECTS,
  HOLDINGS,
  PROPERTY_TYPES_HOME,
  PROPERTY_TYPES_LAP,
  VEHICLE_DEALERS,
  VEHICLE_TYPES,
  floatingRatePct,
  productById,
} from '@/mock/loanData';
import { emiFor, feeFor, gstOn } from '@/utils/loans';
import { formatIndianCurrency } from '@/utils/currency';
import Button from '@/components/ui/Button/Button';
import { ErrorLine, EmptyLine, inr } from './loanShared';

const EMI_DAYS = [1, 5, 10];
const REPAYMENT_METHODS: Array<{ id: RepaymentMethod; label: string }> = [
  { id: 'AUTO_DEBIT', label: 'Auto-debit from my NovaBank account' },
  { id: 'ENACH', label: 'eNACH from another bank' },
  { id: 'UPI_AUTOPAY', label: 'UPI Autopay' },
];
const PURITIES = [18, 20, 22, 24];
const GRACE_CHOICES = [6, 12, 18, 24];

type Step = 'details' | 'review';

const ApplyFlow = () => {
  const { productId = '' } = useParams();
  const navigate = useNavigate();
  const product = useMemo(() => {
    try {
      return productById(productId as never);
    } catch {
      return null;
    }
  }, [productId]);

  const [step, setStep] = useState<Step>('details');
  const [amount, setAmount] = useState('');
  const [tenure, setTenure] = useState(product?.tenureMinMonths ?? 12);
  const [tenureDays, setTenureDays] = useState(15);
  const [emiDay, setEmiDay] = useState(5);
  const [method, setMethod] = useState<RepaymentMethod>('AUTO_DEBIT');
  const [insurance, setInsurance] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Product-specific details
  const [d, setD] = useState<LoanDetails>({});
  const set = (patch: Partial<LoanDetails>) => setD((prev) => ({ ...prev, ...patch }));

  if (!product) {
    return (
      <div className="nova-loans-page">
        <EmptyLine>This loan product does not exist.</EmptyLine>
      </div>
    );
  }

  const amountRupees = Number(amount) || 0;
  const amountPaise = Math.round(amountRupees * 100);
  const isTerm = !product.isRevolving && product.id !== 'SALARY_ADVANCE' && d.repaymentKind !== 'BULLET';
  const needsEmiDay = isTerm && product.id !== 'GOLD';

  const detailsError = (): string => {
    if (!amountRupees) return 'Enter the amount you need.';
    if (amountRupees < product.minRupees || amountRupees > product.maxRupees) {
      return `${product.name} is available from ${formatIndianCurrency(product.minRupees)} to ${formatIndianCurrency(product.maxRupees)}.`;
    }
    switch (product.id) {
      case 'HOME':
        if (!d.propertyType) return 'Choose the property type.';
        if (!d.propertyValueRupees) return 'Enter the property value.';
        break;
      case 'LAP':
        if (!d.propertyType) return 'Choose the property type.';
        if (!d.propertyValueRupees) return 'Enter the property value.';
        if (!d.lapPurpose) return 'What is the loan for?';
        break;
      case 'VEHICLE':
        if (!d.vehicleType) return 'Choose the vehicle.';
        if (!d.onRoadPriceRupees) return 'Enter the on-road price.';
        break;
      case 'EDUCATION':
        if (!d.course || !d.institute) return 'Enter the course and institute.';
        if (!d.indiaOrAbroad) return 'Is the course in India or abroad?';
        break;
      case 'GOLD': {
        if (!d.repaymentKind) return 'Choose how you want to repay.';
        const grams = (d.ornamentGrams ?? 0) + (d.coinGrams ?? 0);
        if (!grams) return 'Enter the weight of the gold.';
        if (!d.purityK) return 'Choose the purity.';
        break;
      }
      case 'LAMF':
      case 'LAS':
        if ((d.pledgedHoldingIds ?? []).length === 0) return 'Choose the holdings to pledge.';
        break;
      default:
        break;
    }
    return '';
  };

  const onReview = () => {
    const msg = detailsError();
    setError(msg);
    if (!msg) setStep('review');
  };

  const onSubmit = async () => {
    setBusy(true);
    setError('');
    try {
      const loan = await applyForLoan({
        productId: product.id,
        amountRupees,
        tenureMonths: product.id === 'SALARY_ADVANCE' ? undefined : tenure,
        tenureDays: product.id === 'SALARY_ADVANCE' ? tenureDays : undefined,
        insuranceOpted: insurance,
        emiDay: needsEmiDay ? emiDay : undefined,
        repaymentMethod: method,
        details: d,
      });
      navigate(`/borrow/applications/${loan.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The application failed.');
      setStep('details');
    } finally {
      setBusy(false);
    }
  };

  // ---- review figures (indicative; the KFS on the application has the final ones) ----
  const indicativeRate = product.spreadPct !== undefined ? floatingRatePct(product) : undefined;
  const indicativeEmi =
    isTerm && indicativeRate !== undefined
      ? emiFor(amountPaise, indicativeRate / 12, tenure)
      : undefined;
  const feePaise = feeFor(product, amountPaise);
  const gstPaise = gstOn(feePaise);

  const toggleHolding = (id: string) => {
    const ids = new Set(d.pledgedHoldingIds ?? []);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    set({ pledgedHoldingIds: [...ids] });
  };

  const num = (v: string): number | undefined => (v ? Number(v) : undefined);

  return (
    <div className="nova-loans-page">
      <div className="nova-loans-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => (step === 'review' ? setStep('details') : navigate(`/borrow/products/${product.id}`))}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{step === 'details' ? `${product.name} — details` : 'Review your application'}</h2>
      </div>

      {step === 'details' && (
        <div className="nova-loan-form-card">
          <div className="nova-loan-form-grid">
          <label className="nova-loan-field">
            <span>Amount (₹)</span>
            <input
              className="form-control"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
              placeholder={`${product.minRupees.toLocaleString('en-IN')} – ${product.maxRupees.toLocaleString('en-IN')}`}
            />
          </label>

          {product.id === 'SALARY_ADVANCE' ? (
            <label className="nova-loan-field">
              <span>Days until repayment (1–30)</span>
              <input
                className="form-control"
                inputMode="numeric"
                value={tenureDays}
                onChange={(e) => setTenureDays(Math.min(30, Math.max(1, Number(e.target.value.replace(/[^\d]/g, '')) || 1)))}
              />
            </label>
          ) : product.tenureMinMonths ? (
            <label className="nova-loan-field">
              <span>Tenure: {tenure} months</span>
              <input
                type="range"
                className="form-range"
                min={product.tenureMinMonths}
                max={product.tenureMaxMonths}
                step={product.id === 'HOME' || product.id === 'LAP' ? 12 : 3}
                value={tenure}
                onChange={(e) => setTenure(Number(e.target.value))}
              />
            </label>
          ) : null}

          {needsEmiDay && (
            <div className="nova-loan-field">
              <span>EMI day of the month</span>
              <div className="nova-loan-chips">
                {EMI_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`nova-loan-chipbtn ${emiDay === day ? 'is-active' : ''}`}
                    onClick={() => setEmiDay(day)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ---- per-product fields ---- */}
          {(product.id === 'HOME' || product.id === 'LAP') && (
            <>
              <label className="nova-loan-field">
                <span>Property type</span>
                <select
                  className="form-select"
                  value={d.propertyType ?? ''}
                  onChange={(e) => set({ propertyType: e.target.value || undefined })}
                >
                  <option value="">Choose…</option>
                  {(product.id === 'HOME' ? PROPERTY_TYPES_HOME : PROPERTY_TYPES_LAP).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              {product.id === 'HOME' && d.propertyType !== 'Plot plus construction' && (
                <label className="nova-loan-field">
                  <span>Approved project (optional)</span>
                  <select
                    className="form-select"
                    value={d.approvedProject ?? ''}
                    onChange={(e) => set({ approvedProject: e.target.value || undefined })}
                  >
                    <option value="">Not in an approved project</option>
                    {APPROVED_PROJECTS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>
              )}
              <label className="nova-loan-field">
                <span>Property value (₹)</span>
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={d.propertyValueRupees ?? ''}
                  onChange={(e) => set({ propertyValueRupees: num(e.target.value.replace(/[^\d]/g, '')) })}
                />
              </label>
              {product.id === 'LAP' && (
                <div className="nova-loan-field">
                  <span>This loan is for</span>
                  <div className="nova-loan-chips">
                    {(['BUSINESS', 'PERSONAL'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`nova-loan-chipbtn ${d.lapPurpose === p ? 'is-active' : ''}`}
                        onClick={() => set({ lapPurpose: p })}
                      >
                        {p === 'BUSINESS' ? 'My business' : 'Personal use'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {product.id === 'VEHICLE' && (
            <>
              <label className="nova-loan-field">
                <span>Vehicle</span>
                <select
                  className="form-select"
                  value={d.vehicleType ?? ''}
                  onChange={(e) => set({ vehicleType: e.target.value || undefined })}
                >
                  <option value="">Choose…</option>
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              <div className="nova-loan-field">
                <span>Condition</span>
                <div className="nova-loan-chips">
                  {(['NEW', 'USED'] as const).map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`nova-loan-chipbtn ${(d.vehicleCondition ?? 'NEW') === c ? 'is-active' : ''}`}
                      onClick={() => set({ vehicleCondition: c })}
                    >
                      {c === 'NEW' ? 'New' : 'Used'}
                    </button>
                  ))}
                </div>
              </div>
              <label className="nova-loan-field">
                <span>Dealer (optional)</span>
                <select
                  className="form-select"
                  value={d.dealer ?? ''}
                  onChange={(e) => set({ dealer: e.target.value || undefined })}
                >
                  <option value="">Choose…</option>
                  {VEHICLE_DEALERS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              <label className="nova-loan-field">
                <span>On-road price (₹)</span>
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={d.onRoadPriceRupees ?? ''}
                  onChange={(e) => set({ onRoadPriceRupees: num(e.target.value.replace(/[^\d]/g, '')) })}
                />
              </label>
            </>
          )}

          {product.id === 'EDUCATION' && (
            <>
              <label className="nova-loan-field">
                <span>Course</span>
                <input className="form-control" value={d.course ?? ''} onChange={(e) => set({ course: e.target.value })} />
              </label>
              <label className="nova-loan-field">
                <span>Institute</span>
                <input className="form-control" value={d.institute ?? ''} onChange={(e) => set({ institute: e.target.value })} />
              </label>
              <div className="nova-loan-field">
                <span>The course is</span>
                <div className="nova-loan-chips">
                  {(['INDIA', 'ABROAD'] as const).map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={`nova-loan-chipbtn ${d.indiaOrAbroad === w ? 'is-active' : ''}`}
                      onClick={() => set({ indiaOrAbroad: w })}
                    >
                      {w === 'INDIA' ? 'In India' : 'Abroad'}
                    </button>
                  ))}
                </div>
              </div>
              <label className="nova-loan-field">
                <span>Course length (months)</span>
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={d.courseMonths ?? ''}
                  onChange={(e) => set({ courseMonths: num(e.target.value.replace(/[^\d]/g, '')) })}
                />
              </label>
              <label className="nova-loan-field">
                <span>Grace after the course</span>
                <select
                  className="form-select"
                  value={d.graceMonths ?? ''}
                  onChange={(e) => set({ graceMonths: num(e.target.value) })}
                >
                  <option value="">Choose…</option>
                  {GRACE_CHOICES.map((g) => (
                    <option key={g} value={g}>{g} months</option>
                  ))}
                </select>
              </label>
              <label className="nova-loan-consent">
                <input
                  type="checkbox"
                  checked={d.interestMonthly ?? false}
                  onChange={(e) => set({ interestMonthly: e.target.checked })}
                />
                <span>I will pay the interest monthly during the course (EMIs stay smaller later).</span>
              </label>
            </>
          )}

          {product.id === 'GOLD' && (
            <>
              <div className="nova-loan-field">
                <span>Repay as</span>
                <div className="nova-loan-chips">
                  {(['EMI', 'BULLET'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`nova-loan-chipbtn ${d.repaymentKind === k ? 'is-active' : ''}`}
                      onClick={() => set({ repaymentKind: k })}
                    >
                      {k === 'EMI' ? 'Monthly EMIs' : 'One payment at the end'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="nova-loan-duo">
                <label className="nova-loan-field">
                  <span>Ornaments (grams)</span>
                  <input
                    className="form-control"
                    inputMode="numeric"
                    value={d.ornamentGrams ?? ''}
                    onChange={(e) => set({ ornamentGrams: num(e.target.value.replace(/[^\d]/g, '')) })}
                  />
                </label>
                <label className="nova-loan-field">
                  <span>Coins (grams)</span>
                  <input
                    className="form-control"
                    inputMode="numeric"
                    value={d.coinGrams ?? ''}
                    onChange={(e) => set({ coinGrams: num(e.target.value.replace(/[^\d]/g, '')) })}
                  />
                </label>
              </div>
              <label className="nova-loan-field">
                <span>Purity</span>
                <select
                  className="form-select"
                  value={d.purityK ?? ''}
                  onChange={(e) => set({ purityK: num(e.target.value) })}
                >
                  <option value="">Choose…</option>
                  {PURITIES.map((p) => (
                    <option key={p} value={p}>{p} karat</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {(product.id === 'LAMF' || product.id === 'LAS') && (
            <div className="nova-loan-field nova-loan-field-full">
              <span>Holdings to pledge</span>
              {HOLDINGS.filter((h) => (product.id === 'LAS' ? h.type === 'SHARE' || h.type === 'ETF' : h.type !== 'SHARE')).map(
                (h) => (
                  <label key={h.id} className="nova-loan-holding">
                    <input
                      type="checkbox"
                      checked={(d.pledgedHoldingIds ?? []).includes(h.id)}
                      onChange={() => toggleHolding(h.id)}
                    />
                    <span className="nova-loan-holding-text">
                      <strong>{h.name}</strong>
                      <span>
                        {formatIndianCurrency(h.valueRupees)} · lends up to {h.ltvPct}%
                      </span>
                    </span>
                  </label>
                )
              )}
            </div>
          )}

          <div className="nova-loan-field nova-loan-field-full">
            <span>Repay EMIs by</span>
            <div className="nova-loan-methods">
              {REPAYMENT_METHODS.map((m) => (
                <label key={m.id} className="nova-loan-holding">
                  <input
                    type="radio"
                    name="repayment-method"
                    checked={method === m.id}
                    onChange={() => setMethod(m.id)}
                  />
                  <span className="nova-loan-holding-text"><strong>{m.label}</strong></span>
                </label>
              ))}
            </div>
          </div>

          </div>

          <ErrorLine message={error} />
          <Button onClick={onReview}>Review application</Button>
        </div>
      )}

      {step === 'review' && (
        <>
          <div className="nova-loan-review">
            <div className="nova-loan-review-row"><span>Product</span><strong>{product.name}</strong></div>
            <div className="nova-loan-review-row"><span>Amount</span><strong>{inr(amountPaise)}</strong></div>
            {product.id === 'SALARY_ADVANCE' ? (
              <div className="nova-loan-review-row"><span>Repayment</span><strong>In {tenureDays} days</strong></div>
            ) : product.tenureMinMonths ? (
              <div className="nova-loan-review-row"><span>Tenure</span><strong>{tenure} months</strong></div>
            ) : null}
            {indicativeRate !== undefined && (
              <div className="nova-loan-review-row">
                <span>Indicative rate</span>
                <strong>{indicativeRate.toFixed(2)}% p.a.</strong>
              </div>
            )}
            {indicativeEmi !== undefined && (
              <div className="nova-loan-review-row"><span>Indicative EMI</span><strong>{inr(indicativeEmi)}</strong></div>
            )}
            <div className="nova-loan-review-row">
              <span>Processing fee + GST</span>
              <strong>{feePaise === 0 ? 'None' : inr(feePaise + gstPaise)}</strong>
            </div>
            {needsEmiDay && (
              <div className="nova-loan-review-row"><span>EMI day</span><strong>{emiDay} of the month</strong></div>
            )}
          </div>

          {product.id === 'PERSONAL' && (
            <label className="nova-loan-consent">
              <input type="checkbox" checked={insurance} onChange={(e) => setInsurance(e.target.checked)} />
              <span>Add loan insurance — the loan is repaid if something happens to me.</span>
            </label>
          )}

          <p className="nova-loan-note">
            Submitting sends your application for review. The Key Fact Statement — your final rate, every fee and
            your cooling-off right — is on the next screen before the bank decides.
          </p>

          <ErrorLine message={error} />
          <Button onClick={onSubmit} isLoading={busy}>Submit application</Button>
        </>
      )}
    </div>
  );
};

export default ApplyFlow;
